import { InsurersCodeEnum } from '../../../../enums/insurers-code.enum';

const EXCLUDED_POLICY_STATUSES = new Set([
  'CANCELED',
  'POLICY_EXPIRED',
  'CANCELATION_REQUEST',
]);

export function canSelectPolicyForTransfer(status: string): boolean {
  if (!status) {
    return true;
  }
  return !EXCLUDED_POLICY_STATUSES.has(status);
}

export interface TransferBatchInput {
  status: string;
  insurerCode?: string;
}

export interface TransferBatchEligibility {
  allowed: boolean;
  reason?: string;
}

export function canTransferBatch(batch: TransferBatchInput): TransferBatchEligibility {
  if (batch.insurerCode && batch.insurerCode !== InsurersCodeEnum.Yelum) {
    return {
      allowed: false,
      reason: 'Transferência disponível apenas para lotes Yelum.',
    };
  }

  if (batch.status === 'PROCESSING') {
    return {
      allowed: false,
      reason:
        'Não é possível transferir enquanto o lote está em faturamento (PROCESSING).',
    };
  }

  return { allowed: true };
}

export function canOpenTransferModal(
  selectedCount: number,
  batchEligibility?: TransferBatchEligibility,
): {
  allowed: boolean;
  reason?: string;
} {
  if (batchEligibility && !batchEligibility.allowed) {
    return batchEligibility;
  }

  if (selectedCount === 0) {
    return {
      allowed: false,
      reason: 'Selecione ao menos uma apólice para transferir',
    };
  }
  return { allowed: true };
}

export function allPreviewItemsMatched(
  items: Array<{ status: string }>,
): boolean {
  return (
    items.length > 0 &&
    items.every((item) => item.status === 'matched')
  );
}

export function isTransferablePreviewStatus(
  status: string,
  recreateMissingQuotes = false,
): boolean {
  if (status === 'matched') {
    return true;
  }
  return recreateMissingQuotes && status === 'not_found';
}

export function getMatchedPreviewItems<T extends { status: string }>(
  items: T[],
): T[] {
  return items.filter((item) => item.status === 'matched');
}

/**
 * Itens com match direto ("matched") exigem o proposalCode do lote destino
 * para atualizar o certificado. Itens recriados (recreateMissingQuotes) não
 * dependem dele — por isso só avisamos/bloqueamos quando existem itens
 * "matched" e o destino não tem proposalCode.
 */
export function needsProposalCodeForTransfer<T extends { status: string }>(
  items: T[],
  hasProposalCode: boolean,
): boolean {
  return !hasProposalCode && getMatchedPreviewItems(items).length > 0;
}

export function getTransferablePreviewItems<T extends { status: string }>(
  items: T[],
  recreateMissingQuotes = false,
): T[] {
  return items.filter((item) =>
    isTransferablePreviewStatus(item.status, recreateMissingQuotes),
  );
}

export function getTransferablePolicyIds(
  items: Array<{ policyId: string; status: string }>,
  recreateMissingQuotes = false,
): string[] {
  return getTransferablePreviewItems(items, recreateMissingQuotes).map(
    (item) => item.policyId,
  );
}

export function hasTransferablePreviewItems(
  items: Array<{ status: string }>,
  recreateMissingQuotes = false,
): boolean {
  return getTransferablePreviewItems(items, recreateMissingQuotes).length > 0;
}

export function summarizeTransferPreview(
  items: Array<{ status: string }>,
  recreateMissingQuotes = false,
): {
  total: number;
  transferable: number;
  remaining: number;
  toRecreate: number;
} {
  const transferable = getTransferablePreviewItems(
    items,
    recreateMissingQuotes,
  ).length;
  const toRecreate = recreateMissingQuotes
    ? items.filter((item) => item.status === 'not_found').length
    : 0;
  return {
    total: items.length,
    transferable,
    remaining: items.length - transferable,
    toRecreate,
  };
}

export function buildTransferConfirmationMessage(
  transferableCount: number,
  remainingCount: number,
  options: { recreateMissingQuotes?: boolean; toRecreateCount?: number } = {},
): string {
  const remainingMessage =
    remainingCount > 0
      ? ` ${remainingCount} apólice(s) com problema permanecerão no lote atual.`
      : '';

  const recreateMessage =
    options.recreateMissingQuotes && (options.toRecreateCount ?? 0) > 0
      ? ` ${options.toRecreateCount} item(ns) serão recriados na cotação destino; se a recriação falhar, o item permanece no lote atual com o erro exibido.`
      : '';

  return (
    `Serão transferidas ${transferableCount} apólice(s).${remainingMessage}${recreateMessage} ` +
    'Ao confirmar: os códigos das apólices serão atualizados conforme o lote destino; ' +
    'itens recriados ficam como "Apólice a faturar" (sem certificado até a próxima transmissão); ' +
    'o lote origem pode ser fechado automaticamente se ficar sem apólices ativas; ' +
    'certificados de itens já existentes serão baixados da Yelum e podem ficar pendentes para reprocessamento noturno.'
  );
}

export function buildTransferCompletionMessage(
  items: Array<{ status: string }>,
): string {
  const doneCount = items.filter((item) => item.status === 'done').length;
  const failedCount = items.filter((item) => item.status === 'failed').length;
  const certErrors = items.filter(
    (item) => item.status === 'certificate_error',
  ).length;
  const budgetErrors = items.filter(
    (item) => item.status === 'budget_error',
  ).length;
  const documentErrors = items.filter(
    (item) => item.status === 'document_error',
  ).length;

  const parts: string[] = [];
  if (doneCount > 0) {
    parts.push(`${doneCount} concluída(s)`);
  }
  if (failedCount > 0) {
    parts.push(
      `${failedCount} permaneceram no lote origem por falha na recriação`,
    );
  }
  if (certErrors > 0) {
    parts.push(
      `${certErrors} com certificado pendente de reprocessamento noturno`,
    );
  }
  if (budgetErrors > 0) {
    parts.push(`${budgetErrors} com orçamento pendente`);
  }
  if (documentErrors > 0) {
    parts.push(`${documentErrors} com limpeza de certificado antigo pendente`);
  }

  if (parts.length === 0) {
    return 'Transferência concluída.';
  }

  return `Transferência concluída: ${parts.join('; ')}.`;
}

export const TRANSFER_CONFIRMATION_MESSAGE =
  'Ao confirmar: os códigos das apólices serão atualizados conforme o lote destino; ' +
  'o lote origem pode ser fechado automaticamente se ficar sem apólices ativas; ' +
  'certificados serão baixados da Yelum e podem ficar pendentes para reprocessamento noturno.';
